import { post } from '../api.js';
import { generateRegistrationKeysPasswordless, wrapMasterKeyWithPrf, clearKeys } from '../crypto.js';
import { navigate } from '../router.js';
import { startRegistration, activatePrf, finishActivatePrf } from '../webauthn.js';
import { lucideIcons, esc } from '../app.js';
import { setToken, getPostAuthRedirect, clearPostAuthRedirect } from '../auth-store.js';

export function renderSetupPasskey(container) {
  container.innerHTML = `
    <div class="login-card">
      <div class="logo">
        <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-setup-pk" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-setup-pk)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-setup-pk)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-setup-pk)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <div class="brand">Blind<span>Keep</span></div>
      </div>
      <p style="color:var(--text-secondary);margin-bottom:1.5rem;text-align:center">
        Register a passkey to secure your vault. If your authenticator supports it, you'll get fully passwordless access.
      </p>
      <div id="setup-msg"></div>
      <form id="passkey-form">
        <div class="field"><label>Passkey name</label><input type="text" name="name" placeholder='e.g. "MacBook Touch ID"' required></div>
        <button type="submit" class="btn btn-primary" style="margin-top:8px">Register Passkey</button>
      </form>
      <div id="prf-step" style="display:none;margin-top:1rem">
        <p style="color:var(--text-secondary);margin-bottom:0.75rem;text-align:center">
          Passkey registered. Tap below to enable passwordless unlock with Face ID / Touch ID.
        </p>
        <button id="prf-activate-btn" class="btn btn-primary" style="width:100%">Enable Passwordless Unlock</button>
        <button id="prf-skip-btn" class="btn btn-secondary" style="width:100%;margin-top:0.5rem">Skip — use a password instead</button>
      </div>
      <div id="fallback" style="display:none;margin-top:1.5rem">
        <p id="fallback-msg" style="color:var(--text-secondary);margin-bottom:0.5rem"></p>
        <button id="fallback-retry" class="btn btn-secondary" style="width:100%;margin-bottom:0.5rem;display:none">Try Again with iCloud Keychain</button>
        <button id="fallback-password" class="btn btn-secondary" style="width:100%">Set a Password Instead</button>
      </div>
    </div>
  `;
  lucideIcons();

  const msg = document.getElementById('setup-msg');
  const form = document.getElementById('passkey-form');
  const prfStep = document.getElementById('prf-step');
  const fallback = document.getElementById('fallback');

  document.getElementById('fallback-password').onclick = () => navigate('#/setup-password');
  document.getElementById('fallback-retry').onclick = () => {
    fallback.style.display = 'none';
    form.style.display = '';
    form.querySelector('button').disabled = false;
    msg.innerHTML = '<div class="msg">Switch to iCloud Keychain in Settings \u2192 Passwords \u2192 Password Options, then register again.</div>';
  };

  // State carried from step 1 (registration) to step 2 (PRF activation)
  let regResult = null;
  let regKeys = null;

  // Step 1: Register the passkey
  form.onsubmit = async (e) => {
    e.preventDefault();
    const name = form.name.value.trim();
    const btn = form.querySelector('button');
    btn.disabled = true;
    msg.innerHTML = '';

    try {
      regKeys = generateRegistrationKeysPasswordless();
      msg.innerHTML = '<div class="msg">Registering passkey... follow the browser prompt.</div>';
      regResult = await startRegistration(name);

      // Registration succeeded — show step 2 with a fresh button
      // so the PRF get() call has its own user gesture.
      form.style.display = 'none';
      prfStep.style.display = '';
      msg.innerHTML = '<div class="msg msg-success">Passkey registered.</div>';
    } catch (err) {
      clearKeys();
      regKeys = null;
      if (err.name === 'NotAllowedError') {
        msg.innerHTML = '<div class="msg msg-error">Passkey operation was cancelled.</div>';
      } else if (err.name === 'TypeError' || err.message?.includes('publicKey')) {
        msg.innerHTML = '<div class="msg msg-error">Passkeys are not supported in this browser.</div>';
        form.style.display = 'none';
        fallback.style.display = '';
      } else {
        msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
      }
      btn.disabled = false;
    }
  };

  // Step 2: Activate PRF (separate user gesture for iOS Safari)
  document.getElementById('prf-activate-btn').onclick = async () => {
    const btn = document.getElementById('prf-activate-btn');
    btn.disabled = true;

    try {
      msg.innerHTML = '<div class="msg">Activating passwordless unlock... follow the browser prompt.</div>';
      const prfResult = await activatePrf(regResult.credential_id, regResult.prf_salt);

      if (!prfResult.prf_output) {
        clearKeys();
        regKeys = null;
        prfStep.style.display = 'none';
        fallback.style.display = '';
        document.getElementById('fallback-msg').textContent =
          'Passwordless unlock requires iCloud Keychain. If your passkey was saved to a third-party manager (e.g. Bitwarden), go to Settings \u2192 Passwords \u2192 Password Options and select iCloud Keychain as your provider, then try again.';
        document.getElementById('fallback-retry').style.display = '';
        msg.innerHTML = '<div class="msg msg-error">Passwordless unlock is not available with this credential provider.</div>';
        return;
      }

      const wrapped = wrapMasterKeyWithPrf(prfResult.prf_output);
      await finishActivatePrf(prfResult.state_id, prfResult.assertion, wrapped);

      const auth = await post('/auth/setup-keys', regKeys);
      setToken(auth.token);
      msg.innerHTML = '<div class="msg msg-success">Passkey enrolled with passwordless unlock!</div>';

      const redirect = getPostAuthRedirect();
      clearPostAuthRedirect();
      navigate(redirect || '#/items');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        msg.innerHTML = '<div class="msg msg-error">Cancelled. Tap the button to try again.</div>';
        btn.disabled = false;
      } else {
        msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        btn.disabled = false;
      }
    }
  };

  // Skip PRF — fall back to password
  document.getElementById('prf-skip-btn').onclick = () => {
    clearKeys();
    regKeys = null;
    navigate('#/setup-password');
  };
}
